import {observer} from "mobx-react-lite";
import React, {useState} from "react";
import {useStores} from "../hooks/useStores";
import {Knob} from "react-svg-knob";
import {
    CUSTOM_ORDER_ASC,
    CUSTOM_ORDER_DEFAULT,
    CUSTOM_ORDER_DESC,
    CUSTOM_ORDER_RANDOM,
    Modulator
} from "../stores/Modulator";
import {invLogValue, logValue} from "../utils/maths";
import {PublicKnobSkinType} from "react-svg-knob/dist/skin";

interface ModulatorControlsProps {
    modulator: Modulator;           // "CC<CC>" by default
    index: number;
}

const PERIOD_RANGE_SEC = 4;       // 0.1sec 1sec
const PERIOD_RANGE_SEC_MIN = 3;   // 1sec 1min
const PERIOD_RANGE_MIN_HOUR = 2;  // 1min 1h
const PERIOD_RANGE_HOUR_DAY = 1;  // 1h 1day

const KNOB_MIN = 0;
const KNOB_MAX = 100;

const pad = (value: number, zero=false) => {
    if (!value && !zero)
        return ''
    else
        return (value < 10 ? `0${value}` : `${value}`);
}

function formatPeriod(s: number): string {
    if (s < 1) {
        // return s.toFixed(3);
        return `${Math.round(s*1000)}ms`;
    } else if (s < 60) {
        return s.toFixed(2) + 's';
    }
    s = Math.round(s);
    const h = Math.floor((s / (60 * 60)) % 60);
    const hours = `${h}`;
    let o = hours ? `${hours}h` : '';
    const minutes = pad(Math.floor((s / 60) % 60), s >= 60);
    if (hours || minutes) o = o + `${minutes}m`;
    if (s < 3600) {
        const seconds = pad(Math.floor(s % 60), true);
        o = o + `${seconds}`;
    }
    return o;
}

function getPeriodRange(range: number) {
    let min;
    let max;
    switch (range) {
        case PERIOD_RANGE_HOUR_DAY:
            min = (24 * 60 * 60);
            max = (60 * 60);
            break;
        case PERIOD_RANGE_MIN_HOUR:
            min = (60 * 60);
            max = 60;
            break;
        case PERIOD_RANGE_SEC:
            min = 1;
            max = 0.1;
            break;
        case PERIOD_RANGE_SEC_MIN:
        default:
            min = 60;
            max = 1;
            break;
    }
    return [min, max];
}

function getPeriodRangeFromFreq(f: number): number {
    if (f > 1) {
        return PERIOD_RANGE_SEC;
    } else if (f > 1/60) {
        return PERIOD_RANGE_SEC_MIN;
    } else if (f > 1/(60*60)) {
        return PERIOD_RANGE_MIN_HOUR;
    } else {
        return PERIOD_RANGE_HOUR_DAY;
    }
}

// horrible hack, but no time to do something better yet
// Return the factor to multiply the modulator frequency
function periodChangeFactor(pfrom: number, pto: number): number {
    switch (pfrom) {
        case PERIOD_RANGE_SEC:
            switch (pto) {
                case PERIOD_RANGE_SEC_MIN:  return 1 / 60;
                case PERIOD_RANGE_MIN_HOUR: return 1 / (60 * 60);
                case PERIOD_RANGE_HOUR_DAY: return 1 / (60 * 60 * 24);
                case PERIOD_RANGE_SEC:      return 1;
                default:
                    // console.warn("invalid period range", pto);
                    return 1;       // invalid
            }
        case PERIOD_RANGE_SEC_MIN:
            switch (pto) {
                case PERIOD_RANGE_SEC_MIN:  return 1;
                case PERIOD_RANGE_MIN_HOUR: return 1 / 60;
                case PERIOD_RANGE_HOUR_DAY: return 1 / (60 * 60);
                case PERIOD_RANGE_SEC:      return 60;
                default:
                    // console.warn("invalid period range", pto);
                    return 1;       // invalid
            }
        case PERIOD_RANGE_MIN_HOUR:
            switch (pto) {
                case PERIOD_RANGE_SEC_MIN:  return 60;
                case PERIOD_RANGE_MIN_HOUR: return 1;
                case PERIOD_RANGE_HOUR_DAY: return 1 / (24);
                case PERIOD_RANGE_SEC:      return 60 * 60;
                default:
                    // console.warn("invalid period range", pto);
                    return 1;       // invalid
            }
        case PERIOD_RANGE_HOUR_DAY:
            switch (pto) {
                case PERIOD_RANGE_SEC_MIN:  return 1 * 60 * 24;
                case PERIOD_RANGE_MIN_HOUR: return 1 * 24;
                case PERIOD_RANGE_HOUR_DAY: return 1;
                case PERIOD_RANGE_SEC:      return 1 * 60 * 60 * 24;
                default:
                    // console.warn("invalid period range", pto);
                    return 1;       // invalid
            }
        default:
            // console.warn("invalid period range", pfrom);
            return 1;       // invalid
    }
}

const knobSkin: PublicKnobSkinType = {
    cursor_length: 15,
    font_size: 15,
    font_weight: "normal",
    track_color: "#0075ff",
    cursor_color: "#0075ff"
};

export const ModulatorControls = observer(({modulator, index}: ModulatorControlsProps) => {

    // console.log("ModulatorControls render");

    const {modulatorsStore: modulators} = useStores();

    const onChangeOffset = (e: React.FormEvent<HTMLInputElement>) => {
        modulator.setOffset(parseFloat(e.currentTarget.value));
    }

    const onChangeAmplitude = (e: React.FormEvent<HTMLInputElement>) => {
        modulator.setAmplitude(parseFloat(e.currentTarget.value));
    }

    const setAmplitudeMax = () => {
        modulator.setAmplitude(1);
    }

    const setOffsetMiddle = () => {
        modulator.setOffset(0.5);
    }

    const setOffsetMin = () => {
        modulator.setOffsetMin();
    }

    const setOffsetMax = () => {
        modulator.setOffsetMax();
    }

    const selectPeriodRange = (range: number) => {
        // console.log("selectPeriodRange", range);
        const fact = periodChangeFactor(periodRange, range);    // from, to
        modulators.setFreq(modulator.freq * fact, index);
        setPeriodRange(range);
    }

    // knob handler
    const updatePeriod = (v: number) => {
        //TODO: choice linear/log
        // console.log("updatePeriod", periodRange);
        // knobPosition = v;
        let lg = logValue(v, KNOB_MIN, KNOB_MAX, pmin, pmax);
        modulators.setFreq(1 / lg, index);
    }

    const knobValueFromFreq = (linear = false): number => {
        const p = 1 / modulator.freq;
        if (linear) {
            return (p - pmin) * (KNOB_MAX - KNOB_MIN) / (pmax - pmin);
        } else {
            return invLogValue(p, KNOB_MIN, KNOB_MAX, pmin, pmax);
        }
    }

    const knobValueFormater = (v: number) => {
        // console.log("knobValueFormater", v);
        let p = logValue(v, KNOB_MIN, KNOB_MAX, pmin, pmax);
        return formatPeriod(p);
    }

    const freqValueForDisplay = (): string => {
        switch (periodRange) {
            case PERIOD_RANGE_HOUR_DAY:
                return `${modulator.freq.toFixed(6)}`;
            case PERIOD_RANGE_MIN_HOUR:
                return `${modulator.freq.toFixed(4)}`;
            case PERIOD_RANGE_SEC:
                return `${modulator.freq.toFixed(1)}`;
            case PERIOD_RANGE_SEC_MIN:
            default:
                return `${modulator.freq.toFixed(2)}`;
        }
    }

    const [periodRange, setPeriodRange] = useState(getPeriodRangeFromFreq(modulator.freq));
    const [pmin, pmax]: number[] = getPeriodRange(periodRange);

    const setCustomOrder = (order: null|string): void => {
        modulator.setCustomOrder(order);
    }

    return (
        <div className="mod-controls-grid-6x4">

            <div className={`mod-controls-overlay ${modulator.isCustomValues() ? 'no-ctrl' : ''}`}>
                <div onClick={() => setCustomOrder(CUSTOM_ORDER_DEFAULT)} className={`${modulator.customOrder === CUSTOM_ORDER_DEFAULT ? 'selected-range' : ''}`}>as entered</div>
                <div onClick={() => setCustomOrder(CUSTOM_ORDER_ASC)} className={`${modulator.customOrder === CUSTOM_ORDER_ASC ? 'selected-range' : ''}`}>ascending</div>
                <div onClick={() => setCustomOrder(CUSTOM_ORDER_DESC)} className={`${modulator.customOrder === CUSTOM_ORDER_DESC ? 'selected-range' : ''}`}>descending</div>
                <div onClick={() => setCustomOrder(CUSTOM_ORDER_RANDOM)} className={`${modulator.customOrder === CUSTOM_ORDER_RANDOM ? 'selected-range' : ''}`}>random</div>
            </div>

            <div className={`row-span-3 ${modulator.isCustomValues() ? 'no-ctrl' : ''}`}>
                <input type="range" className="vertical-slider" min="0" max="1" step="0.01" value={modulator.amplitude} onChange={onChangeAmplitude} />
            </div>
            <div className={`self-top ${modulator.isCustomValues() ? 'no-ctrl' : ''}`}>
                <button onClick={setAmplitudeMax}>max</button>
            </div>
            <div></div>
            <div className={`row-span-3 ${modulator.isCustomValues() ? 'no-ctrl' : ''}`}>
                <input type="range" className="vertical-slider" min="0" max="1" step="0.01" value={modulator.offset} onChange={onChangeOffset} />
            </div>
            <div className={`self-top ${modulator.isCustomValues() ? 'no-ctrl' : ''}`}>
                <button onClick={setOffsetMax}>max</button>
            </div>
            <div></div>
            <div className="row-span-3">
                <div style={{"width":"102px"}}>
                    <Knob initialValue={knobValueFromFreq()} onKnobChange={updatePeriod}
                          config={{format: knobValueFormater, value_resolution: 0.01}}
                          skin={knobSkin}/>
                </div>
            </div>

            <div className="row-span-3 mod-controls-speed">
                <div onClick={() => selectPeriodRange(PERIOD_RANGE_HOUR_DAY)} className={`${periodRange === PERIOD_RANGE_HOUR_DAY ? 'selected-range' : ''}`}>24h ... 1h</div>
                <div onClick={() => selectPeriodRange(PERIOD_RANGE_MIN_HOUR)} className={`${periodRange === PERIOD_RANGE_MIN_HOUR ? 'selected-range' : ''}`}>1h ... 1min</div>
                <div onClick={() => selectPeriodRange(PERIOD_RANGE_SEC_MIN)} className={`${periodRange === PERIOD_RANGE_SEC_MIN ? 'selected-range' : ''}`}>1min ... 1sec</div>
                <div onClick={() => selectPeriodRange(PERIOD_RANGE_SEC)} className={`${periodRange === PERIOD_RANGE_SEC ? 'selected-range' : ''}`}>1sec ... 0.1sec</div>
            </div>

            <div></div>
            <div></div>

            <div className={`self-center ${modulator.isCustomValues() ? 'no-ctrl' : ''}`}>
                <button onClick={setOffsetMiddle}>mid</button>
            </div>
            <div></div>

            {/*<div>mid</div>*/}
            <div></div>
            <div></div>

            <div className={`self-bottom ${modulator.isCustomValues() ? 'no-ctrl' : ''}`}>
                <button onClick={setOffsetMin}>min</button>
            </div>
            <div></div>

            {/*<div>slow</div>*/}
            <div className={`col-span-2 h ${modulator.isCustomValues() ? 'no-ctrl' : ''}`}>amplitude</div>

            <div></div>
            <div className={`col-span-2 h ${modulator.isCustomValues() ? 'no-ctrl' : ''}`}>offset</div>

            <div></div>
            <div className="h">period</div>
            <div className="h normal">f: {freqValueForDisplay()}Hz</div>
        </div>
    );

});
